
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Clock, MapPin, AlertCircle, Bike, CalendarDays } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

interface BikeMovementDetailsProps {
  bikeId: string | null;
  open: boolean;
  onClose: () => void;
  timeframe: string;
}

interface BikeMovementPoint {
  id: string;
  timestamp: string;
  latitude: number;
  longitude: number;
  speed?: number;
}

interface BikeDetails {
  id: string;
  model: string;
  serial_number: string;
  status: string;
  gps_tag?: string;
}

export const BikeMovementDetails: React.FC<BikeMovementDetailsProps> = ({
  bikeId,
  open,
  onClose,
  timeframe
}) => {
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState<BikeMovementPoint[]>([]);
  const [bikeDetails, setBikeDetails] = useState<BikeDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (bikeId && open) {
      fetchBikeMovementDetails();
    }
  }, [bikeId, open, timeframe]);

  const fetchBikeMovementDetails = async () => {
    if (!bikeId) return;
    
    setLoading(true);
    try {
      // Получаем текущую дату и дату "timeframe" часов назад
      const now = new Date();
      const pastDate = new Date(now.getTime() - parseInt(timeframe) * 60 * 60 * 1000);

      // Получаем информацию о велосипеде
      const { data: bikeData, error: bikeError } = await supabase
        .from('bikes')
        .select('id, model, serial_number, status, gps_tag')
        .eq('id', bikeId)
        .single();

      if (bikeError) throw bikeError;

      if (bikeData) {
        setBikeDetails(bikeData);
      }

      // Получаем историю движения велосипеда за выбранный период
      const { data: movementData, error: movementError } = await supabase
        .from('bike_tracking_history')
        .select('id, created_at, latitude, longitude, speed')
        .eq('bike_id', bikeId)
        .gte('created_at', pastDate.toISOString())
        .order('created_at', { ascending: true });

      if (movementError) throw movementError;

      if (movementData) {
        const formattedMovements = movementData.map(point => ({
          id: point.id,
          timestamp: point.created_at,
          latitude: point.latitude,
          longitude: point.longitude,
          speed: point.speed || 0
        }));
        setMovements(formattedMovements);
      }
    } catch (err: any) {
      console.error("Ошибка при получении деталей движения велосипеда:", err);
      setError(err.message || "Произошла ошибка при загрузке данных");
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd.MM.yyyy HH:mm:ss', { locale: ru });
    } catch (e) {
      return 'Некорректная дата';
    }
  };

  const formatRelativeTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ru });
    } catch (e) {
      return 'Некорректная дата';
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Bike className="mr-2 h-5 w-5 text-tuktuk-teal" />
            {loading ? (
              <Skeleton className="h-6 w-40" />
            ) : (
              `Движение велосипеда: ${bikeDetails?.model || ''} (${bikeDetails?.serial_number || ''})`
            )}
          </DialogTitle>
          <DialogDescription>
            {loading ? (
              <Skeleton className="h-4 w-full mt-2" />
            ) : (
              <>
                <div className="flex items-center mt-2">
                  <Badge variant={bikeDetails?.status === 'available' ? 'success' : 'secondary'} className="mr-2">
                    {bikeDetails?.status === 'available' ? 'Доступен' : 'Не доступен'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    GPS Тег: {bikeDetails?.gps_tag || 'Не задан'}
                  </span>
                </div>
                <div className="mt-1 text-sm">
                  <CalendarDays className="inline-block h-4 w-4 mr-1" />
                  Данные за последние {timeframe} часов
                </div>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center p-8 text-red-500">
            <AlertCircle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        ) : movements.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
            <MapPin className="h-12 w-12 mb-2 opacity-40" />
            <p>Нет данных о перемещениях за выбранный период</p>
          </div>
        ) : (
          <>
            <div className="text-sm mb-2">
              <span className="font-medium">Всего точек:</span> {movements.length}
              {movements.length > 0 && (
                <>
                  <span className="mx-2">•</span>
                  <span className="font-medium">Первое движение:</span> {formatRelativeTime(movements[0].timestamp)}
                  <span className="mx-2">•</span>
                  <span className="font-medium">Последнее движение:</span> {formatRelativeTime(movements[movements.length - 1].timestamp)}
                </>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/4">Время</TableHead>
                    <TableHead>Координаты</TableHead>
                    <TableHead className="text-right">Скорость</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((movement) => (
                    <TableRow key={movement.id}>
                      <TableCell>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 text-muted-foreground" />
                          <span>{formatTimestamp(movement.timestamp)}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(movement.timestamp)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1 text-blue-500" />
                          <span className="font-mono text-xs">
                            {movement.latitude.toFixed(6)}, {movement.longitude.toFixed(6)}
                          </span>
                        </div>
                        <a 
                          href={`https://maps.google.com/?q=${movement.latitude},${movement.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:underline"
                        >
                          Открыть на карте
                        </a>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={movement.speed && movement.speed > 5 ? 'destructive' : 'outline'}>
                          {movement.speed ? `${movement.speed.toFixed(1)} км/ч` : 'Нет данных'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 text-sm text-muted-foreground">
              <p className="flex items-center">
                <AlertCircle className="h-4 w-4 mr-1 text-amber-500" />
                Обратите внимание: Движение велосипеда со статусом "Доступен" может означать несанкционированное использование.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
